'use client'
import NextLink from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { Popover, Transition } from '@headlessui/react'
import Image from 'next/image'
import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react'
import { BrandLogo, SvgNotificationIcon } from '@/components/icons/index'
import AvatarWithName from '@/components/ui/avatars/avatar-w-name'
import NovuNotificationCenter from '@/components/ui/novu-notification-center'
import useSubscriptionInfo from '@/hooks/useSubscriptionInfo'
import authSelector from '@/redux/selectors/auth'
import { setAuthenticated, setUserPending } from '@/redux/slices/auth'
import { SendNotification } from '@/services/novu-notifications'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import { useDispatch, useSelector } from 'react-redux'
import { twMerge } from 'tailwind-merge'
import { IHomePageTemplateHeaderNavLink } from '@/types/firebase'
import { defaultHomePageTemplateHeaderNavLinks } from '@/utils/home-page-templates'
import { useRegistrationModal } from '@/app/providers/RegistrationModalProvider'
import { trackEventMixpanel } from '@/utils/mixpanel'
import { INTERACTIVE_PAPER_FEATURE_BUTTON_CLICKED } from '@/utils/mixpanel/eventNames'

const marketingSiteHost = process.env.NEXT_PUBLIC_MARKETING_SITE_HOST || ''

interface Props {
  className?: string
  textColor?: string
  onClickLogoLink?: string
  headerNavLinks?: IHomePageTemplateHeaderNavLink[]
  hasCustomHeaderNavLinks?: boolean
  templateId?: string
}

export default function Header({
  className = '',
  textColor = '',
  onClickLogoLink = '',
  headerNavLinks = defaultHomePageTemplateHeaderNavLinks,
  hasCustomHeaderNavLinks = false,
  templateId,
}: Props) {
  const isConditionMet = useRef(false)

  const router = useRouter()
  const dispatch = useDispatch()
  const { openModal } = useRegistrationModal()

  const searchParams = useSearchParams()
  const useWpRedirect = !!searchParams?.get('useWpRedirect')

  const { user, isUserPending } = useSelector(authSelector)
  const {
    subscription: studentsSubscription,
    subscriptionFromSponsor,
    isUnderSponsorSubscription,
  } = useSubscriptionInfo(user)

  const subscription = useMemo(
    () =>
      isUnderSponsorSubscription && subscriptionFromSponsor
        ? subscriptionFromSponsor
        : studentsSubscription,
    [isUnderSponsorSubscription, studentsSubscription, subscriptionFromSponsor],
  )

  useEffect(() => {
    if (subscription?.role === 'boost' && !isConditionMet.current) {
      SendNotification(
        user?.uid,
        'To gain access to your own Intelligent AI Tutor Companion, try our Boost + plan',
      )
      isConditionMet.current = true
    }
  }, [subscription?.role, user?.uid])

  const goToUserProfile = () => {
    if (!user?.role) return
    const role = user.role.toLowerCase()
    switch (role) {
      case 'admin':
        router.replace('/admin')
        break
      case 'student':
        dispatch(setAuthenticated(true))
        dispatch(setUserPending(false))
        router.replace('/student')
        break
      case 'tutor':
        router.replace('/tutors-dashboard')
        break
      case 'parent':
      case 'guardian':
        router.replace('/parent')
        break
      case 'schooladmin':
        router.replace('/school/members')
        break
      default:
        break
    }
  }

  const renderLogInButtons = () => {
    const loginBtnClassName = useWpRedirect
      ? 'h-12 py-2 px-4 text-sm uppercase font-medium text-white border-8 border-wpIndigo-100 rounded-full shadow-sm disabled:opacity-60 disabled:cursor-not-allowed bg-wpIndigo-500 focus:outline-none focus:ring-offset-0 focus:ring-0 hover:opacity-60 transition duration-300 flex items-center justify-center gap-1'
      : 'duration-300 text-sm xl:text-base font-semibold border border-v8-line rounded-md h-full bg-white text-v8-ink hover:border-v8-blue px-4'

    const signUpBtnClassName = useWpRedirect
      ? 'h-12 py-2 px-4 text-sm uppercase font-medium text-white border-8 border-wpBlue-100 rounded-full shadow-sm disabled:opacity-60 disabled:cursor-not-allowed bg-wpBlue-500 focus:outline-none focus:ring-offset-0 focus:ring-0 hover:opacity-60 transition duration-300 flex items-center justify-center gap-1'
      : 'duration-300 text-sm xl:text-base font-semibold rounded-md h-full bg-v8-blue text-white hover:bg-v8-violet shadow-v8-btn px-4'

    return (
      <div className="flex flex-wrap gap-2.5 items-center">
        <button
          onClick={() => openModal({ isRegisterForm: false })}
          className={loginBtnClassName}
        >
          Log in
        </button>

        <button onClick={() => openModal()} className={signUpBtnClassName}>
          Sign Up for Free
        </button>
      </div>
    )
  }

  const renderProfileButtons = () => {
    return (
      <div className="flex items-center gap-4 rounded-full px-5 py-1 mt-3 text-[12px] font-semibold bg-cgray-250 text-gray-50 mr-2 mb-2">
        <NovuNotificationCenter />
        <button
          className="overflow-hidden rounded-full"
          onClick={() => goToUserProfile()}
        >
          {user?.photoURL ? (
            <div className="relative rounded-full h-9 w-9">
              <Image
                className="object-cover rounded-full h-9 w-9"
                src={user?.photoURL || ''}
                fill
                alt="profile icon"
              />
            </div>
          ) : (
            <AvatarWithName
              className="text-sm bg-v8-blue/15 rounded-full h-9 w-9"
              textClassName="text-v8-blue"
              name={user?.name ? user.name : 'Adam'}
            />
          )}
        </button>
      </div>
    )
  }

  const generateHrefPrefixDependingOnUserRole = useCallback(() => {
    const prefixByRoleObj = {
      ['tutor']: '/tutor',
      ['student']: '/student',
      ['parent']: '/parent',
    }

    let prefix = ''

    if (user && user.role) {
      prefix = prefixByRoleObj[user.role]
    }

    return prefix
  }, [user])

  return (
    <>
      <Popover
        className={twMerge(
          'relative',
          'sticky top-0 bg-white border-b border-v8-line z-[250]',
          className,
        )}
      >
        <div
          className="absolute inset-0 z-[230] pointer-events-none"
          aria-hidden="true"
        />
        <div className="relative z-[220]">
          <div
            className={`h-[var(--header-height-in-px)] mx-auto max-w-7xl p-2 md:px-6 md:py-4 lg:px-8 flex items-center justify-between lg:justify-start `}
          >
            <div>
              <NextLink
                href={user?.role ? `/${user.role}` : 'https://examsolutions.net'}
                className="md:-mx-2 flex focus:outline-none"
              >
                <span className="sr-only">ExamSolutions Logo</span>
                <div className="flex items-center flex-shrink-0">
                  <BrandLogo className="h-10" />
                </div>
              </NextLink>
            </div>
            <div className="hidden lg:flex gap-2 xl:gap-8 flex-1 max-xl:justify-around xl:ml-10">
              {hasCustomHeaderNavLinks ? (
                headerNavLinks.map((link, index) => (
                  <NextLink
                    key={`{${index}-${link.title}}`}
                    href={link.href}
                    className={`text-nowrap text-sm xl:text-base hover:text-gray-900 duration-300 font-medium ${
                      textColor ? textColor : 'text-cgray-500'
                    } relative `}
                  >
                    {link.title}
                  </NextLink>
                ))
              ) : (
                <>
                  <div className="relative group flex items-center">
                    <button
                      type="button"
                      className={`text-nowrap text-sm xl:text-base hover:text-gray-900 duration-300 font-medium ${
                        textColor ? textColor : 'text-cgray-500'
                      }`}
                    >
                      Learn
                    </button>
                    <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 absolute left-0 top-full pt-3 transition-all duration-200 z-[240]">
                      <div className="bg-white rounded-xl shadow-lg ring-1 ring-gray-200 py-2 w-56 flex flex-col">
                        <NextLink href="/courses" className="px-4 py-2 text-sm text-cgray-700 hover:bg-gray-50 hover:text-gray-900">
                          Guided courses
                        </NextLink>
                        <NextLink href="/maths/" className="px-4 py-2 text-sm text-cgray-700 hover:bg-gray-50 hover:text-gray-900">
                          Video lessons <span style={{ fontSize: 10.5, color: '#8b93a7' }}>maths</span>
                        </NextLink>
                        <NextLink href="/gcse-maths/" className="px-4 py-2 text-sm text-cgray-700 hover:bg-gray-50 hover:text-gray-900">
                          GCSE Maths
                        </NextLink>
                        <NextLink href="/a-level-maths/" className="px-4 py-2 text-sm text-cgray-700 hover:bg-gray-50 hover:text-gray-900">
                          A Level Maths
                        </NextLink>
                        <NextLink href="/international-exams/" className="px-4 py-2 text-sm text-cgray-700 hover:bg-gray-50 hover:text-gray-900">
                          International exams
                        </NextLink>
                      </div>
                    </div>
                  </div>

                  <div className="relative group flex items-center">
                    <button
                      type="button"
                      className={`text-nowrap text-sm xl:text-base hover:text-gray-900 duration-300 font-medium ${
                        textColor ? textColor : 'text-cgray-500'
                      }`}
                    >
                      Practice
                    </button>
                    <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 absolute left-0 top-full pt-3 transition-all duration-200 z-[240]">
                      <div className="bg-white rounded-xl shadow-lg ring-1 ring-gray-200 py-2 w-56 flex flex-col">
                        <NextLink
                          href="/exam-sphere"
                          className="px-4 py-2 text-sm text-cgray-700 hover:bg-gray-50 hover:text-gray-900"
                          onClick={() => {
                            trackEventMixpanel(
                              INTERACTIVE_PAPER_FEATURE_BUTTON_CLICKED,
                              { source: 'App Homepage' },
                            )
                          }}
                        >
                          ExamSphere
                        </NextLink>
                        <NextLink
                          href="/papers?tab=examPapers"
                          className="px-4 py-2 text-sm text-cgray-700 hover:bg-gray-50 hover:text-gray-900"
                        >
                          Past papers
                        </NextLink>
                        <NextLink
                          href="/solution-bank-template-v8-preview/"
                          className="px-4 py-2 text-sm text-cgray-700 hover:bg-gray-50 hover:text-gray-900"
                        >
                          Solution bank <span style={{ fontSize: 10.5, color: '#8b93a7' }}>maths</span>
                        </NextLink>
                      </div>
                    </div>
                  </div>

                  <NextLink
                    href="/ask-axi"
                    className={`text-nowrap text-sm xl:text-base hover:text-gray-900 duration-300 font-medium ${
                      textColor ? textColor : 'text-cgray-500'
                    }`}
                  >
                    Ask Axi{' '}
                    <sup className="text-[9px] font-bold text-orange-500">
                      AI tutor
                    </sup>
                  </NextLink>

                  <NextLink
                    href="/pricing"
                    className={`text-nowrap text-sm xl:text-base hover:text-gray-900 duration-300 font-medium ${
                      textColor ? textColor : 'text-cgray-500'
                    }`}
                  >
                    Pricing
                  </NextLink>
                </>
              )}

            </div>
            <div className="flex items-center gap-2 -my-2 -mr-1.5 pr-2 md:pr-0 lg:hidden">
              {!user && !isUserPending && (
                <button
                  onClick={() => openModal()}
                  className="px-3 py-1.5 text-sm font-semibold text-white bg-v8-blue rounded-lg hover:bg-v8-violet transition duration-300"
                >
                  Sign Up
                </button>
              )}

              {user && (
                <button className="text-cgray-500 p-2">
                  <SvgNotificationIcon stroke="#667085" />
                </button>
              )}

              <Popover.Button className="inline-flex items-center justify-center p-2 text-gray-400 transition duration-300 bg-white rounded-md hover:bg-cgray-50 focus:outline-none md:focus:ring-2 md:focus:ring-inset md:focus:ring-v8-blue ">
                <span className="sr-only">Open menu</span>
                <Bars3Icon className="w-6 h-6" aria-hidden="true" />
              </Popover.Button>
            </div>
            <div className="hidden lg:flex lg:items-center lg:justify-end">
              {isUserPending
                ? ''
                : user
                ? renderProfileButtons()
                : renderLogInButtons()}
            </div>
          </div>
        </div>

        <Transition
          as={Fragment}
          enter="duration-200 ease-out"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="duration-100 ease-in"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Popover.Panel
            focus
            className="absolute inset-x-0 top-0 z-[230] p-2 transition origin-top-right transform lg:hidden"
          >
            <div className="bg-white divide-y-2 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 divide-gray-50">
              <div className="px-5 pt-5 pb-6 sm:pb-8">
                <div className="flex  ">
                  <div className="flex flex-wrap w-full ">
                    <NextLink
                      href={user?.role ? `/${user.role}` : 'https://examsolutions.net'}
                      className="flex items-center flex-shrink-0 px-4 mr-auto"
                      aria-label="ExamSolutions home"
                    >
                      <BrandLogo />
                    </NextLink>
                    <div className="w-fit lg:items-center ">
                      {user ? renderProfileButtons() : ''}
                    </div>
                  </div>
                  <div className={`${user ? 'mt-3' : ''} -mr-2 `}>
                    <Popover.Button className="inline-flex items-center justify-center p-2 text-gray-400 bg-white rounded-md hover:text-gray-500 hover:bg-gray-100 focus:outline-none md:focus:ring-2 md:focus:ring-inset md:focus:ring-v8-blue">
                      <span className="sr-only">Close menu</span>
                      <XMarkIcon className="w-6 h-6" aria-hidden="true" />
                    </Popover.Button>
                  </div>
                </div>
                <div className="mt-6 sm:mt-8">
                  <nav className="pt-6">
                    <div className="grid sm:grid-cols-2 sm:gap-x-4">
                      {hasCustomHeaderNavLinks ? (
                        headerNavLinks.map((link, index) => (
                          <NextLink
                            key={`{${index}-${link.title}}`}
                            href={link.href}
                            className="flex items-center p-3 rounded-lg hover:bg-gray-50 relative"
                          >
                            <div className="ml-4 text-base font-medium text-cgray-700">
                              {link.title}
                            </div>
                          </NextLink>
                        ))
                      ) : (
                        <>
                          <div className="px-3 pt-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
                            Learn
                          </div>
                          <NextLink href="/courses" className="flex items-center p-3 rounded-lg hover:bg-gray-50">
                            <div className="ml-4 text-base font-medium text-cgray-700">Guided courses</div>
                          </NextLink>
                          <NextLink href="/maths/" className="flex items-center p-3 rounded-lg hover:bg-gray-50">
                            <div className="ml-4 text-base font-medium text-cgray-700">Video lessons <span style={{ fontSize: 10.5, color: '#8b93a7' }}>maths</span></div>
                          </NextLink>
                          <NextLink href="/gcse-maths/" className="flex items-center p-3 rounded-lg hover:bg-gray-50">
                            <div className="ml-4 text-base font-medium text-cgray-700">GCSE Maths</div>
                          </NextLink>
                          <NextLink href="/a-level-maths/" className="flex items-center p-3 rounded-lg hover:bg-gray-50">
                            <div className="ml-4 text-base font-medium text-cgray-700">A Level Maths</div>
                          </NextLink>
                          <NextLink href="/international-exams/" className="flex items-center p-3 rounded-lg hover:bg-gray-50">
                            <div className="ml-4 text-base font-medium text-cgray-700">International exams</div>
                          </NextLink>
                          <div className="px-3 pt-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
                            Practice
                          </div>
                          <NextLink
                            href="/exam-sphere"
                            className="flex items-center p-3 rounded-lg hover:bg-gray-50"
                            onClick={() => {
                              trackEventMixpanel(
                                INTERACTIVE_PAPER_FEATURE_BUTTON_CLICKED,
                                { source: 'App Homepage' },
                              )
                            }}
                          >
                            <div className="ml-4 text-base font-medium text-cgray-700">
                              ExamSphere
                            </div>
                          </NextLink>
                          <NextLink
                            href="/papers?tab=examPapers"
                            className="flex items-center p-3 rounded-lg hover:bg-gray-50"
                          >
                            <div className="ml-4 text-base font-medium text-cgray-700">
                              Past papers
                            </div>
                          </NextLink>
                          <NextLink
                            href="/solution-bank-template-v8-preview/"
                            className="flex items-center p-3 rounded-lg hover:bg-gray-50"
                          >
                            <div className="ml-4 text-base font-medium text-cgray-700">
                              Solution bank <span style={{ fontSize: 10.5, color: '#8b93a7' }}>maths</span>
                            </div>
                          </NextLink>
                          <NextLink
                            href="/ask-axi"
                            className="flex items-center p-3 rounded-lg hover:bg-gray-50"
                          >
                            <div className="ml-4 text-base font-medium text-cgray-700">
                              Ask Axi{' '}
                              <sup className="text-[9px] font-bold text-orange-500">
                                AI tutor
                              </sup>
                            </div>
                          </NextLink>
                          <NextLink
                            href="/pricing"
                            className="flex items-center p-3 rounded-lg hover:bg-gray-50"
                          >
                            <div className="ml-4 text-base font-medium text-cgray-700">
                              Pricing
                            </div>
                          </NextLink>
                        </>
                      )}
                    </div>
                  </nav>
                </div>
              </div>

              {!user && (
                <div className="px-5 pb-6">
                  <div className="">
                    <button
                      onClick={() => openModal()}
                      className="flex items-center justify-center w-full px-4 py-2 text-base font-medium text-white border border-transparent rounded-md shadow-sm v8-violet-button md:hover:text-v8-violet"
                    >
                      Sign Up for Free
                    </button>
                    <p className="mt-6 text-base font-medium text-center text-gray-">
                      Existing customer?&nbsp;
                      <button
                        onClick={() => openModal({ isRegisterForm: false })}
                        className="text-v8-violet"
                      >
                        Sign In
                      </button>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Popover.Panel>
        </Transition>
      </Popover>
    </>
  )
}
